import * as React from 'react';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';

import { CustomEventNames } from 'features/analytics';
import { logNavigationClick } from 'features/navigation/utils/analytics';
import { EmptyStateActions as ActionOptions } from 'features/store/components/store-locator-empty-states/types';
import { StyleSheet } from 'react-native';

import ActionButton from 'components/action-button';
import { GeolocationDeniedModal } from 'components/geolocation-denied-modal';
import { Box } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { ModalAuthTrigger } from 'state/auth/types';
import { useGeolocation } from 'state/geolocation';
import { t } from 'state/intl/translations';
import { spacing } from 'styles/constants';

/**
 *
 * EmptyStateAction enables displaying action options, i.e. a Sign In button, for different empty states
 *
 */
export const EmptyStateAction: FC<React.PropsWithChildren<{ action: ActionOptions }>> = ({
  action,
}) => {
  const { isPermissionPrompt, isPermissionDenied, setPermissionChecking } = useGeolocation();
  const isDesktop = useIsDesktopBreakpoint();

  const { openSignInModal } = useAuthContext();

  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = useState(false);

  const onSignInClick = useCallback(() => {
    openSignInModal({
      trigger: ModalAuthTrigger.Modal,
    });

    logNavigationClick(CustomEventNames.BUTTON_CLICK_SIGN_IN);
  }, [openSignInModal]);

  const handleGeolocationPrompt = useCallback(() => {
    if (isPermissionDenied) {
      setGeolocationDeniedModalOpen(true);
    } else if (isPermissionPrompt) {
      setPermissionChecking();
    }
  }, [isPermissionDenied, isPermissionPrompt, setPermissionChecking]);

  const dismissModal = () => setGeolocationDeniedModalOpen(false);

  const ActionComponent: ReactNode | null = useMemo(() => {
    switch (action) {
      case ActionOptions.LOGIN:
        return (
          <ActionButton onPress={onSignInClick} testID="signin-button">
            {t('signIn')}
          </ActionButton>
        );
      case ActionOptions.SHARE_LOCATION:
        return (
          <>
            <GeolocationDeniedModal
              isOpen={geolocationDeniedModalOpen}
              dismissModal={dismissModal}
            />
            <ActionButton testID="share-location-button" onPress={handleGeolocationPrompt}>
              {t('shareMyLocation')}
            </ActionButton>
          </>
        );
      default:
        return null;
    }
  }, [action, geolocationDeniedModalOpen, handleGeolocationPrompt, onSignInClick]);

  if (!ActionComponent) {
    return null;
  }

  return (
    <Box
      style={[styles.wrapper, isDesktop && styles.wrapperDesktop]}
      testID="empty-state-action-wrapper"
    >
      {ActionComponent}
    </Box>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    justifyContent: 'center',
    marginBottom: spacing.$3,
  },
  wrapperDesktop: {
    marginBottom: spacing.$4,
  },
});
